import React, { useEffect, useState } from "react";
import logo from "../assets/logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import welcome9 from "./splashscreens/57.png";
import google from "../assets/icons/google.png";
import { useGoogleLogin } from '@react-oauth/google';
import { Capacitor } from "@capacitor/core";

export default function SignUp({ onSignIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [hasViewedPolicy, setHasViewedPolicy] = useState(false);
  const [strength, setStrength] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleConsentOpen, setIsGoogleConsentOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { signup, signinWithGoogle, refreshCompletionFlags } = useAuth();
  const isNativePlatform = Capacitor.isNativePlatform();

  const resolvePostAuthRoute = async () => {
    try {
      const flags = await refreshCompletionFlags();
      const hasProfile = Boolean(flags?.hasProfile);
      const hasAssessment = Boolean(flags?.hasAssessment);

      if (!hasProfile) return '/profile-setup';
      
      return '/home';
    } catch {
      
      return '/profile-setup';
    }
  };

  const launchGoogleSignup = useGoogleLogin({
    flow: 'implicit',
    ux_mode: isNativePlatform ? 'redirect' : 'popup',
    redirect_uri: `${window.location.origin}/signup`,
    scope: 'openid email profile',
    onSuccess: async (tokenResponse) => {
      try {
        const result = await signinWithGoogle(tokenResponse?.access_token, 'access_token');

        const inviteCode = getPendingInviteCode();
        if (inviteCode && result?.user?.email) {
          try {
            await api.post(`/api/referrals/accept/${encodeURIComponent(inviteCode)}`, {
              email: result.user.email,
            });
            localStorage.removeItem('pending_invite_code');
          } catch (acceptErr) {
            console.error('Failed to accept referral invite:', acceptErr);
          }
        }

        const nextRoute = result?.created ? '/health-assessment' : await resolvePostAuthRoute();
        toast.success(result?.created ? 'Google account created. Continuing to health assessment.' : 'Signed in with Google 🎉');
        navigate(nextRoute);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Google sign up failed. Please try again.');
      } finally {
        setIsGoogleLoading(false);
      }
    },
    onError: () => {
      setIsGoogleLoading(false);
      toast.error('Google sign up failed. Please try again.');
    },
    onNonOAuthError: (nonOAuthError) => {
      setIsGoogleLoading(false);
      if (nonOAuthError?.type === 'popup_closed') {
        toast.warning('Google popup was closed before completion.');
        return;
      }
      if (nonOAuthError?.type === 'popup_failed_to_open') {
        toast.error(isNativePlatform ? 'Google sign-up window could not be opened on this device. Please try again.' : 'Popup was blocked. Please allow popups and try again.');
        return;
      }
      toast.error('Google sign up could not be started. Please try again.');
    },
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const acceptedFromQuery = params.get("policyAccepted") === "1";
    const acceptedFromStorage = localStorage.getItem("signup_terms_accepted") === "true";

    if (acceptedFromQuery || acceptedFromStorage) {
      setHasViewedPolicy(true);
      setAgreeTerms(true);
      return;
    }

    setHasViewedPolicy(false);
    setAgreeTerms(false);
    navigate("/terms-policy?from=signup", { replace: true });
  }, [location.search]);

  const getPendingInviteCode = () => {
    const params = new URLSearchParams(location.search);
    const fromQuery = (params.get('ref') || '').trim();
    const fromStorage = (localStorage.getItem('pending_invite_code') || '').trim();
    return fromQuery || fromStorage || '';
  };

  const checkPasswordStrength = (pass) => {
    let score = 0;
    if (pass.length >= 8) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^A-Za-z0-9]/.test(pass)) score++;
    if (score <= 1) return "Weak";
    if (score === 2) return "Medium";
    return "Strong";
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setStrength(checkPasswordStrength(val));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!hasViewedPolicy) {
      toast.warning("Please read Terms & Privacy Policy first");
      navigate("/terms-policy?from=signup");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!agreeTerms) {
      toast.warning("Please agree to the terms");
      return;
    }

    try {
      await signup({ email, password });

      const inviteCode = getPendingInviteCode();
      if (inviteCode) {
        try {
          await api.post(`/api/referrals/accept/${encodeURIComponent(inviteCode)}`, {
            email,
          });
          localStorage.removeItem('pending_invite_code');
        } catch (acceptErr) {
          console.error('Failed to accept referral invite:', acceptErr);
        }
      }

      toast.success("Account created 🎉");
      navigate(await resolvePostAuthRoute());
    } catch (err) {
      console.error("Signup error:", err);
      const msg = err?.response?.data?.message || err?.message || "Sign up failed. Please try again.";
      toast.error(msg);
      alert("Signup error: " + msg);
    }
  };

  const handleGooglePrecheck = () => {
    if (isGoogleLoading) return;

    if (!hasViewedPolicy) {
      toast.warning("Please review Terms & Privacy Policy first");
      navigate("https://qureohealth.com/privacy-policy");
      return;
    }

    if (!agreeTerms) {
      toast.warning("Please agree to the terms before Google signup");
      return;
    }

    setIsGoogleConsentOpen(true);
  };

  const handleGoogleConsentContinue = () => {
    setIsGoogleConsentOpen(false);
    setIsGoogleLoading(true);
    try {
      launchGoogleSignup();
    } catch {
      setIsGoogleLoading(false);
      toast.error('Google sign up could not be started. Please try again.');
    }
  };

  return (
    <div
      className="container"
      style={{
        minHeight: "100vh",
        backgroundImage : "var(--bg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "22px 16px",
      }}
    >
      {isGoogleConsentOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            backdropFilter: "blur(5px)",
            zIndex: 1001,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div style={googleConsentCard}>
            <h4 style={googleConsentTitle}>Continue with Google</h4>
            <p style={googleConsentText}>
              Qureo will request access to your Google basic profile data: name, email address, and profile picture.
            </p>
            <p style={googleConsentSubtext}>
              This data is used only for account creation, secure sign in, and personalizing your care experience. Qureo never posts to your Google account.
            </p>
            <button type="button" onClick={() => navigate("https://qureohealth.com/privacy-policy")} style={googleConsentPolicyBtn}>
              Review Terms and Privacy Policy
            </button>
            <div style={googleConsentActions}>
              <button type="button" onClick={() => setIsGoogleConsentOpen(false)} style={googleCancelBtn}>
                Cancel
              </button>
              <button type="button" onClick={handleGoogleConsentContinue} style={googleContinueBtn}>
                I Understand, Continue
              </button>
            </div>
          </div>
        </div>
      )}

       <div style = {{ width: "100%", maxWidth: "360px", background : "white", borderRadius: "22px", padding: "16px"  }}>

      <div style={{ width: "100%", maxWidth: "360px" }}>
    

        <div style={{ textAlign: "center", marginBottom: "18px" }}>
          <h5 style={{ color: "#000000", fontSize: "28px", fontWeight: 800, lineHeight: 1.05, marginBottom: "8px" }}>
            Create your account
          </h5>
          <p style={{ color: "#6b7280", fontSize: "12px", fontWeight: 500, margin: 0 }}>
            Use your email to create a secure
            <br />
            Qureo account.
          </p>
        </div>

        <form onSubmit={handleSignUp}>
          <div style={cardWrap}>
            <div style={{ marginBottom: "12px" }}>
              <label style={fieldLabel}>Email Address</label>
              <div style={inputWrapperMuted}>
                <span style={iconLeftMuted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                  </svg>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={iconInput}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={fieldLabel}>Password</label>
              <div style={inputWrapperMuted}>
                <span style={iconLeftMuted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  placeholder="Enter your password"
                  onChange={handlePasswordChange}
                  required
                  style={iconInput}
                />
                <button type="button" onClick={() => setShowPassword((v) => !v)} style={eyeBtn}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
              {strength && (
                <small
                  style={{
                    fontWeight: 600,
                    color: strength === "Weak" ? "#dc2626" : strength === "Medium" ? "#f59e0b" : "#16a34a",
                    marginTop: 4,
                    display: "block",
                  }}
                >
                  Strength: {strength}
                </small>
              )}
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={fieldLabel}>Confirm Password</label>
              <div style={inputWrapperMuted}>
                <span style={iconLeftMuted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </span>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Confirm your password"
                  style={iconInput}
                />
                <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} style={eyeBtn}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span style={{ fontSize: "12px", color: "#7b8190", fontWeight: 500 }}>
                Your information is encrypted and protected.
              </span>
            </div>
          </div>

          <div style={{ marginBottom: "14px" }}>
            <label style={termsRow}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => {
                  if (!hasViewedPolicy && e.target.checked) {
                    navigate("/terms-policy?from=signup");
                  } else {
                    setAgreeTerms(e.target.checked);
                  }
                }}
                style={checkboxStyle}
              />
              <span style={{ color: "#404756" }}>
                I agree to the
                <button type="button" onClick={() => navigate("https://qureohealth.com/terms-of-use")} style={policyLinkBtn}>
                  Terms of Service
                </button>
                and
                <button type="button" onClick={() => navigate("https://qureohealth.com/privacy-policy")} style={policyLinkBtn}>
                  Privacy Policy
                </button>
              </span>
            </label>
          </div>

          <button type="submit" style={primaryBtn}>
            Create Account
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "8px" }}>
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          <div style={divider}>or</div>

          <button type="button" onClick={() => toast.info("Apple sign up not implemented yet")} style={{ ...socialBtn, opacity: 0.95 }}>
              <img src="https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg" alt="Apple" style={{ height: "18px" }} />
              Continue with Apple
          </button>

          <button type="button" onClick={handleGooglePrecheck} disabled={isGoogleLoading} style={{ ...socialBtn, opacity: isGoogleLoading ? 0.7 : 1 }}>
            <img src={google} alt="Google" style={{ height: "18px" }} />
            {isGoogleLoading ? "Connecting to Google..." : "Continue with Google"}
          </button>

          <button type="button" onClick={handleGooglePrecheck} disabled={isGoogleLoading} style={socialBtn}>
           <i className="fi fi-ss-circle-phone" style={{ height: "18px", color: "var(--primary)" }}></i>
            Continue with phone number
          </button>

          <div style={{ textAlign: "center", fontSize: "13px", marginTop: "6px" }}>
            <span style={{ color: "#e4e4e4" }}>Already have an account?</span>
            <a href="/signin" onClick={onSignIn} style={linkStyle}>
              Sign In
            </a>
          </div>
        </form>
      </div>
      </div>
      <ToastContainer />
    </div>
  );
}

/* 🔹 Shared Styles */
const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  marginTop: "2px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  outline: "none",
  fontSize: "14px",
};

const cardWrap = {
  border: "1px solid #ececf5",
  borderRadius: "14px",
  background: "#fff",
  padding: "14px 12px",
  marginBottom: "14px",
  boxShadow: "0 1px 2px rgba(17, 24, 39, 0.04)",
};

const fieldLabel = {
  fontSize: "13px",
  fontWeight: 700,
  color: "#2f374d",
  marginBottom: "6px",
  display: "block",
};

const inputWrapperEmail = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  border: "1px solid #e4e5ee",
  borderRadius: "10px",
  background: "#fff",
  overflow: "hidden",
};

const inputWrapperMuted = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  border: "1px solid #e4e5ee",
  borderRadius: "10px",
  background: "#fff",
  overflow: "hidden",
};

const iconLeft = {
  display: "flex",
  alignItems: "center",
  paddingLeft: "10px",
  flexShrink: 0,
};

const iconLeftMuted = {
  display: "flex",
  alignItems: "center",
  paddingLeft: "10px",
  flexShrink: 0,
};

const iconInput = {
  flex: 1,
  padding: "12px 10px",
  border: "none",
  outline: "none",
  fontSize: "14px",
  background: "transparent",
  color: "#1f2937",
};

const eyeBtn = {
  display: "flex",
  alignItems: "center",
  paddingRight: "10px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  flexShrink: 0,
};

const termsRow = {
  fontSize: "13px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
};

const checkboxStyle = {
  accentColor: "var(--primary)",
  width: "15px",
  height: "15px",
  cursor: "pointer",
  flexShrink: 0,
};

const policyLinkBtn = {
  border: "none",
  background: "transparent",
  color: "var(--primary)",
  fontWeight: 700,
  fontSize: "13px",
  padding: "0 0 0 4px",
  cursor: "pointer",
};

const primaryBtn = {
  width: "100%",
  padding: "14px",
  borderRadius: "12px",
  border: "none",
  background: "var(--primary)",
  color: "#fff",
  fontSize: "15px",
  fontWeight: 700,
  cursor: "pointer",
  marginBottom: "14px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const divider = {
  textAlign: "center",
  fontSize: "13px",
  color: "#b0b5c2",
  margin: "8px 0 12px",
  fontWeight: 600,
};

const socialBtn = {
  width: "100%",
  height: "48px",
  borderRadius: "10px",
  border: "1px solid #e4e5ee",
  background: "#FFFFFF",
  fontSize: "15px",
  fontWeight: 700,
  color: "#111827",
  cursor: "pointer",
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  transition: "all 0.2s ease",
};



const linkStyle = {
  marginLeft: "6px",
  color: "var(--primary)",
  fontWeight: 700,
  textDecoration: "none",
};

const googleConsentCard = {
  width: "100%",
  maxWidth: "420px",
  background: "#fff",
  borderRadius: "16px",
  border: "1px solid #ececf5",
  boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
  padding: "20px",
};

const googleConsentTitle = {
  margin: 0,
  marginBottom: "10px",
  color: "#1f2945",
  fontSize: "20px",
  fontWeight: 800,
};

const googleConsentText = {
  margin: 0,
  color: "#374151",
  fontSize: "14px",
  lineHeight: 1.5,
};

const googleConsentSubtext = {
  marginTop: "8px",
  marginBottom: "14px",
  color: "#6b7280",
  fontSize: "13px",
  lineHeight: 1.5,
};

const googleConsentPolicyBtn = {
  border: "none",
  background: "transparent",
  color: "var(--primary)",
  fontWeight: 700,
  fontSize: "13px",
  cursor: "pointer",
  padding: 0,
  marginBottom: "16px",
};

const googleConsentActions = {
  display: "flex",
  gap: "10px",
};

const googleCancelBtn = {
  flex: 1,
  height: "42px",
  borderRadius: "10px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  fontWeight: 700,
  cursor: "pointer",
};

const googleContinueBtn = {
  flex: 1.8,
  height: "42px",
  borderRadius: "10px",
  border: "none",
  background: "var(--primary)",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
